import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { isStringEmpty, isStringNotEmpty } from '../utils/objectUtils';
import { RESUME_TOKEN_PARAM, clearPendingResumeToken, getPendingResumeToken } from '../utils/resumeTokenUtils';
import logger from '../utils/logger';
import AuthService from '../firebase/AuthService';
import { useAuth } from '../redux/ducks/auth';

// the auth listener can take a moment to reflect a sign-in, and a guard that decides in that gap would
// bounce a freshly signed-in lead; this is the longest the guard waits for the listener
const SIGN_IN_SETTLE_TIMEOUT_MS = 15000;

/**
 * Redeems an emailed resume token and signs its owner in. The token normally arrives already lifted
 * off the url by the boot capture; one still on the url after an in-app navigation is read and
 * stripped here before it is exchanged.
 *
 * @returns {{ isRedeeming: boolean }} True while a token is still being exchanged.
 */
export const useResumeLinkRedemption = () => {
	//#region useSearchParams

	const [searchParams, setSearchParams] = useSearchParams();

	//#endregion
	//#region useAuth

	const { authContext } = useAuth();

	//#endregion
	//#region useStates

	const [token] = useState(() => getPendingResumeToken() ?? searchParams.get(RESUME_TOKEN_PARAM));
	const [isRedeeming, setIsRedeeming] = useState(isStringNotEmpty(token));
	const [awaitedUserId, setAwaitedUserId] = useState(null);

	//#endregion
	//#region useRefs

	const hasAttempted = useRef(false);

	//#endregion
	//#region useEffects

	useEffect(() => {
		if (isStringEmpty(token) || hasAttempted.current) {
			return;
		}
		hasAttempted.current = true;

		// the token leaves memory and the url before the network call, so nothing can pick it up meanwhile
		clearPendingResumeToken();
		if (searchParams.has(RESUME_TOKEN_PARAM)) {
			setSearchParams((params) => {
				params.delete(RESUME_TOKEN_PARAM);
				return params;
			}, { replace: true });
		}

		const redeem = async () => {
			try {
				const { userId, isAlreadySignedIn, isUserSwitch } = await AuthService.doSignInWithResumeLink(token);
				if (isUserSwitch) {
					// another person was signed in on this browser: reload so none of their state survives
					window.location.reload();
				} else if (isAlreadySignedIn) {
					setIsRedeeming(false);
				} else {
					setAwaitedUserId(userId);
				}
			} catch (error) {
				logger.logErrorEvent('RESUME_LINK_REDEMPTION', error, 'The resume link could not be redeemed', true);
				setIsRedeeming(false);
			}
		};

		redeem();
	}, [token]);

	useEffect(() => {
		if (isStringEmpty(awaitedUserId)) {
			return;
		}
		if (authContext?.currentAuthUser?.uid === awaitedUserId) {
			setAwaitedUserId(null);
			setIsRedeeming(false);
			return;
		}

		const timeout = setTimeout(() => {
			setAwaitedUserId(null);
			setIsRedeeming(false);
		}, SIGN_IN_SETTLE_TIMEOUT_MS);

		return () => clearTimeout(timeout);
	}, [awaitedUserId, authContext?.currentAuthUser?.uid]);

	//#endregion

	return { isRedeeming };
};

export default useResumeLinkRedemption;
